import { Clock, User } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

interface Session {
  id: string;
  playerName: string;
  duration: number;
  shots: number;
  avgQuality: string;
  timestamp: string;
}

interface RecentActivityProps {
  sessions: Session[];
}

export function RecentActivity({ sessions }: RecentActivityProps) {
  const getQualityVariant = (quality: string) => {
    switch (quality) {
      case 'EXCELLENT': return 'success';
      case 'GOOD': return 'default';
      case 'AVERAGE': return 'secondary';
      case 'POOR': return 'destructive';
      default: return 'secondary';
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInHours = Math.floor((now.getTime() - time.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours === 1) return '1 hour ago';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return '1 day ago';
    return `${diffInDays} days ago`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Clock className="mr-2 h-5 w-5" />
          Recent Sessions
        </CardTitle>
        <CardDescription>
          Latest training sessions and performance
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {sessions.map((session) => (
            <div key={session.id} className="flex items-center justify-between border-b pb-4 last:border-b-0">
              <div className="flex items-center space-x-3">
                <Avatar className="h-8 w-8">
                  <AvatarFallback>
                    {session.playerName.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{session.playerName}</p>
                  <p className="text-sm text-muted-foreground">
                    {session.duration}min • {session.shots} shots
                  </p>
                </div>
              </div>
              <div className="text-right space-y-1">
                <Badge variant={getQualityVariant(session.avgQuality) as any}>
                  {session.avgQuality}
                </Badge>
                <p className="text-xs text-muted-foreground">
                  {formatTimeAgo(session.timestamp)}
                </p>
              </div>
            </div>
          ))}
          {sessions.length === 0 && (
            <div className="text-center py-8">
              <User className="mx-auto h-12 w-12 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">
                No recent sessions
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}